<template>
  <div class="supplier-info-container">
    <!-- 左侧蓝色标记和标题 -->
    <div class="page-header">
      <div class="blue-bar"></div>
      <h2 class="page-title">供应商信息管理</h2>
    </div>
    
    <!-- 搜索区域 -->
    <div class="search-area">
      <div class="search-inputs">
        <div class="search-item">
          <el-input
            v-model="searchForm.unitName"
            placeholder="供应商名称"
            prefix-icon="el-icon-search"
            clearable
          ></el-input>
        </div>
        <div class="search-item">
          <el-input
            v-model="searchForm.contactPerson"
            placeholder="联系人"
            clearable
          ></el-input>
        </div>
        <div class="search-item">
          <el-input
            v-model="searchForm.phone"
            placeholder="联系人电话"
            clearable
          ></el-input>
        </div>
        <div class="search-buttons">
          <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
          <el-button v-permission="'basic:supplier-info:create'" type="primary" @click="showAddDialog" icon="el-icon-plus">新增供应商</el-button>
        </div>
      </div>
    </div>
    
    <!-- 表格区域 -->
    <div class="table-container">
      <el-table
        :data="tableData"
        border
        stripe
        style="width: 100%"
        v-loading="loading"
        :header-cell-style="{background:'#f5f7fa', color:'#606266'}"
      >
        <el-table-column prop="unitName" label="供应商名称" min-width="160"></el-table-column>
        <el-table-column prop="contactPerson" label="联系人" width="120">
          <template slot-scope="scope">
            {{ maskName(scope.row.contactPerson) }}
          </template>
        </el-table-column>
        <el-table-column prop="phone" label="联系人电话" width="150">
          <template slot-scope="scope">
            {{ maskPhone(scope.row.phone) }}
          </template>
        </el-table-column>
        <el-table-column prop="taxId" label="税号" width="160"></el-table-column>
        <el-table-column prop="address" label="单位地址" min-width="200"></el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-permission="'basic:supplier-info:delete'"
              size="mini"
              type="text"
              class="delete-btn"
              @click="handleDelete(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 30, 50]"
          :page-size="pagination.pageSize"
          layout="prev, pager, next, jumper, sizes, total"
          :total="pagination.total"
        >
        </el-pagination>
      </div>
    </div>
    
    <!-- 新增对话框 -->
    <el-dialog
      title="添加供应商"
      :visible.sync="dialogVisible"
      width="700px"
      :close-on-click-modal="false"
      :show-close="true"
      center
      class="supplier-dialog"
    >
      <el-form :model="supplierForm" :rules="rules" ref="supplierForm" label-width="100px">
        <el-form-item label="选择单位" prop="unit_id">
          <el-select 
            v-model="supplierForm.unit_id" 
            filterable 
            placeholder="请选择单位" 
            @change="handleUnitChange"
            style="width: 100%">
            <el-option
              v-for="item in unitsList"
              :key="item.id"
              :label="item.unitName"
              :value="item.id">
              <span>{{ item.unitName }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">{{ item.contactPerson }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        
        <!-- 供应商单位信息展示 -->
        <div v-if="selectedUnitInfo" class="unit-info-display">
          <el-divider content-position="left">供应商单位信息</el-divider>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="单位名称" :span="2">
              {{ selectedUnitInfo.unitName || '--' }}
            </el-descriptions-item>
            <el-descriptions-item label="单位简称">
              {{ selectedUnitInfo.unitShortName || '--' }}
            </el-descriptions-item>
            <el-descriptions-item label="简称首字母">
              {{ selectedUnitInfo.shortNameInitial || '--' }}
            </el-descriptions-item>
            <el-descriptions-item label="税号" :span="2">
              {{ selectedUnitInfo.taxId || '--' }}
            </el-descriptions-item>
            <el-descriptions-item label="联系人">
              {{ maskName(selectedUnitInfo.contactPerson) || '--' }}
            </el-descriptions-item>
            <el-descriptions-item label="联系电话">
              {{ maskPhone(selectedUnitInfo.phone) || '--' }}
            </el-descriptions-item>
            <el-descriptions-item label="单位地址" :span="2">
              {{ selectedUnitInfo.address || '--' }}
            </el-descriptions-item>
          </el-descriptions>
        </div>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </div>
    </el-dialog>
    
    <!-- 删除确认对话框 -->
    <el-dialog
      title="提示"
      :visible.sync="deleteDialogVisible"
      width="400px"
      center
      :close-on-click-modal="false"
    >
      <div class="delete-dialog-content">
        <i class="el-icon-warning-outline warning-icon"></i>
        <span>确认删除该供应商信息吗？</span>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="deleteDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="confirmDelete">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/api'
import { query_unit_by_id } from '@/api/modules/unit.js'
import { maskName as maskNameUtil, maskPhone as maskPhoneUtil } from '@/utils/mask.js'

export default {
  name: 'SupplierInfo',
  data() {
    return {
      // 搜索表单
      searchForm: {
        unitName: '',
        contactPerson: '',
        phone: '',
      },
      // 加载状态
      loading: false,
      // 表格数据
      tableData: [],
      // 分页配置
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      // 对话框
      dialogVisible: false,
      deleteDialogVisible: false,
      deleteId: null,
      // 表单数据
      supplierForm: {
        unit_id: null,
      },
      // 当前用户单位ID
      currentUnitId: null,
      // 所有单位列表（用于下拉选择）
      unitsList: [],
      // 选中的单位详细信息
      selectedUnitInfo: null,
      // 表单验证规则
      rules: {
        unit_id: [
          { required: true, message: '请选择单位', trigger: 'change' }
        ]
      }
    }
  },
  created() {
    // 获取当前用户的单位ID
    this.getCurrentUnitId();
  },
  methods: {
    // 获取当前用户的单位ID
    getCurrentUnitId() {
      try {
        const userInfo = this.$store.state.userInfo || {};
        if (userInfo.unitId) {
          this.currentUnitId = userInfo.unitId;
// 获取到单位ID后加载供应商列表
          this.fetchSupplierList();
        } else {
          console.warn('未能从用户信息中获取unitId');
          this.$message.error('无法获取当前单位信息，请重新登录');
        }
      } catch (err) {
        console.error('获取用户unitId出错:', err);
        this.$message.error('获取单位信息失败');
      }
    },
    
    // 获取所有单位列表（用于下拉选择）
    fetchAllUnits() {
      this.loading = true;
      
      // 使用新的API: GET /units
      api.unit.getAllUnits()
        .then(response => {
if (response.data && response.data.success) {
            const list = response.data.data || [];
            // 过滤掉当前用户自己的单位
            this.unitsList = list.filter(unit => unit.id !== this.currentUnitId);
} else {
            this.$message.error(response.data.message || '获取单位列表失败');
          }
        })
        .catch(error => {
          console.error('获取单位列表失败: ', error);
          this.$message.error('获取单位列表失败，请稍后重试');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // 获取供应商列表
    fetchSupplierList() {
      this.loading = true;
      
      // 构建查询参数
      const params = {
        page: this.pagination.currentPage - 1, // 后端从0开始
        size: this.pagination.pageSize,
        sortBy: 'id',
        sortDir: 'desc'
      };
      
      // 添加搜索条件（如果有值）
      if (this.searchForm.unitName && this.searchForm.unitName.trim()) {
        params.unitName = this.searchForm.unitName.trim();
      }
      if (this.searchForm.contactPerson && this.searchForm.contactPerson.trim()) {
        params.contactPerson = this.searchForm.contactPerson.trim();
      }
      if (this.searchForm.phone && this.searchForm.phone.trim()) {
        params.phone = this.searchForm.phone.trim();
      }
      
// 使用新的API: GET /units/{unitId}/suppliers
      api.unit.getSuppliersByUnitId(this.currentUnitId, params)
        .then(response => {
if (response.data && response.data.success) {
            // 根据实际返回的数据结构：data.content
            const responseData = response.data.data;
            this.tableData = responseData.content || [];
            
            // 更新分页信息 - 使用Spring Data分页对象的字段
            this.pagination = {
              currentPage: responseData.number + 1, // 后端从0开始，前端从1开始
              pageSize: responseData.size,
              total: responseData.totalElements
            };
            
// 如果没有数据，显示提示信息
            if (this.tableData.length === 0) {
              this.$message.info('未找到符合条件的供应商信息');
            }
          } else {
            this.$message.error(response.data.message || '查询失败');
          }
        })
        .catch(error => {
          console.error('查询供应商列表失败: ', error);
          this.$message.error('查询供应商列表失败，请稍后重试');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // 搜索方法
    handleSearch() {
      this.pagination.currentPage = 1; // 重置为第一页
      this.fetchSupplierList();
    },
    
    // 显示新增对话框
    showAddDialog() {
      // 先获取所有单位列表
      this.fetchAllUnits();
      this.resetForm();
      this.dialogVisible = true;
    },
    
    // 删除操作
    handleDelete(row) {
      this.deleteId = row.id;
      this.deleteDialogVisible = true;
    },
    
    // 确认删除
    confirmDelete() {
      this.loading = true;
      
      // 使用删除供应商关系的API
      api.unit.removeSupplier(this.currentUnitId, this.deleteId)
        .then(response => {
if (response.data && response.data.success) {
            this.$message.success(response.data.message || '删除成功');
            // 删除成功后重新加载列表
            this.fetchSupplierList();
            this.deleteDialogVisible = false;
          } else {
            this.$message.error(response.data.message || '删除失败');
          }
        })
        .catch(error => {
          console.error('删除供应商失败: ', error);
          this.$message.error('删除供应商失败，请稍后重试');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // 重置表单
    resetForm() {
      this.supplierForm = {
        unit_id: null
      };
      this.selectedUnitInfo = null;
      if (this.$refs.supplierForm) {
        this.$refs.supplierForm.resetFields();
      }
    },
    
    // 处理单位选择变化
    handleUnitChange(unitId) {
      if (!unitId) {
        this.selectedUnitInfo = null;
        return;
      }
      
      // 获取单位详细信息
      query_unit_by_id(unitId)
        .then(response => {
if (response.data && response.data.success) {
            this.selectedUnitInfo = response.data.data;
          } else {
            // 如果API返回失败，尝试从unitsList中查找
            const unit = this.unitsList.find(u => u.id === unitId);
            if (unit) {
              this.selectedUnitInfo = unit;
            } else {
              this.$message.warning('获取单位信息失败');
              this.selectedUnitInfo = null;
            }
          }
        })
        .catch(error => {
          console.error('获取单位详情失败:', error);
          // 如果API调用失败，尝试从unitsList中查找
          const unit = this.unitsList.find(u => u.id === unitId);
          if (unit) {
            this.selectedUnitInfo = unit;
          } else {
            this.$message.error('获取单位信息失败');
            this.selectedUnitInfo = null;
          }
        });
    },
    
    // 提交表单
    submitForm() {
      this.$refs.supplierForm.validate(valid => {
        if (valid) {
          // 检查是否正在添加自己作为供应商
          if (this.supplierForm.unit_id === this.currentUnitId) {
            this.$message.error('不能添加自己为供应商');
            return false;
          }
          
          this.loading = true;
          
          // 使用新的添加供应商API
          api.unit.addSupplier(this.currentUnitId, this.supplierForm.unit_id)
            .then(response => {
if (response.data && response.data.success) {
                this.$message.success('添加供应商成功');
                this.dialogVisible = false;
                // 重新加载列表
                this.fetchSupplierList();
              } else {
                // 显示后台返回的错误消息
                const errorMessage = response.data?.message || '添加供应商失败';
                this.$message.error(errorMessage);
              }
            })
            .catch(error => {
              console.error('添加供应商失败: ', error);
              // 处理HTTP错误响应
              let errorMessage = '添加供应商失败';
              if (error.response && error.response.data) {
                // 优先使用后台返回的错误消息
                errorMessage = error.response.data.message || error.response.data.error || errorMessage;
              } else if (error.message) {
                errorMessage = error.message;
              }
              this.$message.error(errorMessage);
            })
            .finally(() => {
              this.loading = false;
            });
        } else {
          return false;
        }
      });
    },
    
    // 分页大小变化
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.fetchSupplierList();
    },
    
    // 页码变化
    handleCurrentChange(val) {
      this.pagination.currentPage = val;
      this.fetchSupplierList();
    },
    
    // 脱敏姓名
    maskName(name) {
      return maskNameUtil(name);
    },
    
    // 脱敏电话
    maskPhone(phone) {
      return maskPhoneUtil(phone);
    }
  }
}
</script>

<style lang="scss">
/* 全局对话框样式，确保对话框在页面中心 */
.el-dialog__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  
  .el-dialog {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 60px);   /* 限制最大高度 */
    
    .el-dialog__body {
      overflow-y: auto;               /* 内容过多时出现滚动条 */
      flex: 1;
    }
  }
}
</style>

<style lang="scss" scoped>
.supplier-info-container {
  background-color: #fff;
  padding: 20px;
  min-height: calc(100vh - 120px);
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  
  .page-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    
    .blue-bar {
      width: 3px;
      background-color: #1890ff;
      height: 16px;
      margin-right: 8px;
    }
    
    .page-title {
      font-size: 16px;
      font-weight: 500;
      color: #333;
      margin: 0;
    }
  }
  
  .search-area {
    margin-bottom: 20px;
    
    .search-inputs {
      display: flex;
      flex-wrap: wrap;
      
      .search-item {
        margin-right: 15px;
        margin-bottom: 10px;
        width: 200px;
      }
      
      .search-buttons {
        display: flex;
        align-items: flex-start;
        
        .el-button {
          margin-right: 10px;
        }
      }
    }
  }
  
  .table-container {
    .el-table {
      margin-bottom: 20px;
    }
    
    .delete-btn {
      color: #f56c6c;
    }
  }
  
  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }
  
  .delete-dialog-content {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 30px 0;
    
    .warning-icon {
      font-size: 24px;
      color: #e6a23c;
      margin-right: 10px;
    }
    
    span {
      font-size: 16px;
    }
  }
  
  .supplier-dialog {
    :deep(.el-dialog__header) {
      background-color: #f8f9fc;
      padding: 15px 20px;
      text-align: center;
    }
    
    :deep(.el-dialog__title) {
      font-size: 16px;
      font-weight: 500;
      color: #333;
    }
    
    :deep(.el-dialog__body) {
      padding: 20px;
    }
    
    :deep(.el-form-item__label) {
      font-weight: normal;
      color: #606266;
    }
    
    :deep(.el-input__inner) {
      border-radius: 2px;
    }
    
    :deep(.el-dialog__footer) {
      padding: 10px 20px 20px;
      text-align: center;
    }
    
    :deep(.el-dialog__headerbtn) {
      top: 15px;
    }
  }
  
  .unit-info-display {
    margin-top: 20px;
    
    :deep(.el-divider__text) {
      font-size: 14px;
      font-weight: 500;
      color: #606266;
    }
    
    :deep(.el-descriptions) {
      margin-top: 10px;
    }
    
    :deep(.el-descriptions__label) {
      font-weight: 500;
      color: #606266;
    }
    
    :deep(.el-descriptions__content) {
      color: #303133;
    }
  }
  
  // 删除确认对话框样式
  :deep(.el-dialog) {
    border-radius: 4px;
    overflow: hidden;
  }
}
</style> 


